import React from 'react';
import { motion } from 'framer-motion';
import CountUp from '../common/CountUp';
import { aboutContent, aboutFeatures, aboutStats, aboutCards } from '../../data';
import { brandInfo } from '../../data';
import './About.css';

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const About = () => {
  return (
    <section id="about" className="about section">
      <div className="container">
        {/* Header */}
        <motion.div
          className="about-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <span className="section-eyebrow">{aboutContent.label}</span>
          <h2 className="section-heading">
            {aboutContent.title.split(aboutContent.titleHighlight)[0]}
            <span className="accent">{aboutContent.titleHighlight}</span>
          </h2>
        </motion.div>

        {/* Main grid */}
        <div className="about-grid">
          {/* Visual column */}
          <div className="about-visual">
            <div className="about-image-frame">
              <motion.img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=82&auto=format&fit=crop"
                alt="Insite Infra construction site"
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              />
              {/* Curtain slides right to reveal the image */}
              <motion.div
                className="about-image-curtain"
                initial={{ scaleX: 1 }}
                whileInView={{ scaleX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="about-image-caption">
                <h4>{aboutCards[0].title}</h4>
                <p>{aboutCards[0].description}</p>
              </div>
            </div>

            <motion.div
              className="about-badge"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="badge-since">Est. {brandInfo.established}</span>
              <h4>{aboutCards[1].title}</h4>
              <p>{aboutCards[1].description}</p>
            </motion.div>
          </div>

          {/* Text column */}
          <div className="about-content">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              {aboutContent.paragraphs.map((paragraph, index) => (
                <p key={index} className="about-text">
                  {paragraph}
                </p>
              ))}
            </motion.div>

            <div className="about-features">
              {aboutFeatures.map((feature, index) => (
                <motion.div
                  key={feature.number}
                  className="about-feature"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ delay: index * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span className="feature-number">{feature.number}</span>
                  <div className="feature-body">
                    <h4 className="feature-title">{feature.title}</h4>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats band */}
        <motion.div
          className="about-stats"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          {aboutStats.map((stat) => (
            <div key={stat.label} className="about-stat">
              <span className="stat-number">
                <CountUp value={stat.number} />
              </span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
